import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { RiMapPinLine, RiCheckboxCircleLine, RiCloseLine, RiHistoryLine } from 'react-icons/ri';
import siteService, { Site, SiteContactForm } from '../../services/siteService';
import companyService from '../../services/companyService';
import codeGeneratorService from '../../services/codeGeneratorService';
import { useUpdateSite } from '../../hooks/mutations/useSiteMutations';
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges';
import { usePermission } from '../../hooks/usePermission';
import { queryClient, queryKeys } from '../../queryClient';
import { PAGINATION_CONFIG } from '../../config/pagination';
import { SITES_PATH, siteDetailUrl } from '../../utils/siteLinks';
import { parseRouteId } from '../../utils/routeId';
import { showAlert, showConfirm } from '../../utils/alert';
import { mapFieldErrors, focusFirstInvalidField } from '../../utils/mapFieldErrors';
import ConfirmModal from '../../components/ui/ConfirmModal';
import SiteFormFields from '../../components/sites/SiteFormFields';
import { SiteAppCredentialsHandle } from '../../components/sites/SiteAppCredentialsSection';
import {
  emptySiteForm,
  siteToForm,
  siteToContacts,
  validateSiteForm,
  buildSitePayload,
  siteIfMatch,
  SiteFormState,
  SITE_FIELD_LABELS,
} from '../../components/sites/siteForm';
import {
  DetailPageLayout,
  DetailSection,
  RecordMetaCard,
  EntityActivityTimeline,
  useDetailNavigation,
} from '../../components/detail';

const LIST_PATH = SITES_PATH;

/**
 * หน้าเพิ่ม/แก้ไขโครงการ — `/master-data/sites/new` · `/master-data/sites/:id/edit`
 * (แทนโมดัลเพิ่ม/แก้ไขใน SitePage · มติ PO 28 ต.ค. 2026 · ต้นแบบ CompanyFormPage)
 *
 * ตรรกะบันทึกยกจาก handleSaveSite ของโมดัลเดิมครบ: กันกดซ้ำ (ref · BUG-S2101-003) · รหัสซ้ำ → ออกรหัสใหม่ +
 * เตือนใต้ช่อง รอกดอีกครั้ง · ชื่อซ้ำ → ใต้ช่องชื่อ · 422 → ใต้ช่อง (รวมเบอร์ผู้ติดต่อรายแถว) ·
 * If-Match (P-S468-1) · ปิดโครงการที่ยังมีการเช่า/สัญญา → กล่องยืนยัน (pre-check + 409 SITE_HAS_ACTIVE_USAGE) ·
 * บัญชีเข้าระบบแอพบันทึกต่อหลังโครงการสำเร็จ (ต้องมี id)
 */
const SiteFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isEdit = id !== undefined;
  const siteId = isEdit ? parseRouteId(id) : null;
  const { hasPermission } = usePermission();
  // บัญชีเข้าระบบแอพของโครงการ (PO 30 ก.ย. 2026) — บันทึกต่อจากโครงการผ่าน commit(siteId)
  const canManageAppCredentials = hasPermission('sites.app_credentials');
  const appCredRef = useRef<SiteAppCredentialsHandle>(null);
  const { backTo, goTo } = useDetailNavigation(LIST_PATH);

  // State สำหรับควบคุมการแสดง success message (รหัสซ้ำ = ไม่บอกว่าสำเร็จ)
  const shouldShowSuccessRef = useRef(true);
  const createSiteMutation = useMutation({
    mutationFn: (data: any) => siteService.createSite(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.sites.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.rentals.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.customers.all });
      if (shouldShowSuccessRef.current) showAlert('สำเร็จ', 'สร้างโครงการสำเร็จ', 'success');
    },
  });
  const updateSiteMutation = useUpdateSite();

  const [formData, setFormData] = useState<SiteFormState>(() => emptySiteForm());
  const [contacts, setContacts] = useState<SiteContactForm[]>([]);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [codeError, setCodeError] = useState<string>('');
  /** ค่าตั้งต้นของฟอร์ม (JSON) — เทียบเพื่อรู้ว่าผู้ใช้แก้แล้วหรือยัง · null = ยังโหลดไม่เสร็จ */
  const [initialSnapshot, setInitialSnapshot] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const siteQuery = useQuery({
    queryKey: queryKeys.sites.detail(siteId ?? 0),
    queryFn: () => siteService.getSite(siteId!),
    enabled: isEdit && siteId !== null,
    // หน้าแก้ไขต้องได้ token If-Match + ค่านับการเช่าล่าสุดเสมอ — ไม่ใช้ cache เก่าจากหน้าดู
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
  const site: Site | undefined = siteQuery.data;

  const { data: companiesData, isLoading: companiesLoading, isError: companiesError } = useQuery({
    queryKey: queryKeys.companies.list(),
    queryFn: async () => await companyService.getCompanies({
      limit: PAGINATION_CONFIG.DROPDOWN_LIMIT_LARGE, // ดึงข้อมูลทั้งหมดสำหรับ dropdown (companies endpoint รับ max 1000)
      page: 1,
    }),
  });
  const companies = companiesData?.items || [];

  // ตั้งค่าฟอร์มครั้งเดียวต่อโครงการ (refetch ภายหลังไม่ทับสิ่งที่ผู้ใช้กำลังพิมพ์ · 409 ล้างเพื่อโหลดใหม่)
  const initializedFor = useRef<number | 'new' | null>(null);
  useEffect(() => {
    if (isEdit) {
      if (!site || initializedFor.current === site.id) return;
      initializedFor.current = site.id;
      const f = siteToForm(site);
      const c = siteToContacts(site);
      setFormData(f);
      setContacts(c);
      setInitialSnapshot(JSON.stringify([f, c]));
      return;
    }
    if (initializedFor.current === 'new') return;
    initializedFor.current = 'new';
    // ใช้ preview รหัสถัดไปแทนการสร้างรหัสจริง
    codeGeneratorService
      .getNextCodePreview('site', true)
      .then((r) => r.next_code)
      .catch((error) => {
        console.error('Error getting next code preview:', error);
        return ''; // fallback to empty
      })
      .then((code) => {
        const f = emptySiteForm(code);
        setFormData(f);
        setContacts([]);
        setInitialSnapshot(JSON.stringify([f, []]));
      });
  }, [isEdit, site]);

  const isDirty = !saved && initialSnapshot !== null && JSON.stringify([formData, contacts]) !== initialSnapshot;
  const { showModal, onConfirmLeave, onCancelLeave } = useUnsavedChanges(isDirty);

  const cancelTo = isEdit && siteId ? siteDetailUrl(siteId) : backTo;
  const saving = createSiteMutation.isPending || updateSiteMutation.isPending;

  // บันทึกเสร็จ → หน้าดู (replace) พร้อม from เดิม · ไม่มีสิทธิ์ดู = กลับที่เปิดมา
  const finish = (savedId?: number) => {
    setSaved(true);
    if (savedId && hasPermission('sites.view')) goTo(siteDetailUrl(savedId), { replace: true });
    else goTo(backTo, { replace: true });
  };

  // BUG-S2101-003: ดับเบิลคลิก "บันทึก" ยิง POST /sites 2 ครั้ง · isPending เป็น react-state กันไม่ทันใน tick เดียว → ref
  const isSubmittingRef = useRef(false);
  const releaseSubmit = () => { isSubmittingRef.current = false; };
  // บัญชีแอพบันทึกหลังโครงการสำเร็จ (ต้องมี id) — ล้มเหลวไม่ย้อนโครงการ แต่ต้องบอกว่าส่วนนี้ยังไม่ถูกบันทึก
  const commitAppCredentials = async (savedSiteId: number) => {
    try {
      await appCredRef.current?.commit(savedSiteId);
    } catch (e: any) {
      showAlert('บันทึกโครงการแล้ว แต่บัญชีเข้าระบบแอพยังไม่ถูกบันทึก', e?.message || 'กรุณาเปิดโครงการแล้วบันทึกบัญชีแอพอีกครั้ง', 'warning');
    }
  };

  const handleSaveSite = async () => {
    if (isSubmittingRef.current) return;
    const errors = validateSiteForm(formData);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      const errorElement = document.querySelector(`[name="${Object.keys(errors)[0]}"]`);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        (errorElement as HTMLInputElement).focus();
      }
      return;
    }
    setFormErrors({});
    shouldShowSuccessRef.current = true;

    const siteData = buildSitePayload(formData, contacts);

    const appCredError = appCredRef.current?.validate();
    if (appCredError) {
      showAlert('ข้อมูลไม่ถูกต้อง', appCredError, 'error');
      releaseSubmit();
      return;
    }

    if (!isEdit) {
      isSubmittingRef.current = true;
      createSiteMutation.mutate(siteData, {
        onSettled: releaseSubmit,
        onSuccess: async (savedSite: any) => {
          // บัญชีแอพต้องรอ id ของโครงการใหม่ — บันทึกไม่สำเร็จไม่ย้อนโครงการ แต่ต้องบอก
          if (savedSite?.id) await commitAppCredentials(savedSite.id);
          setCodeError('');
          finish(savedSite?.id);
        },
        onError: async (error: any) => {
          console.error('Error creating site:', error);
          // BUG-S1162-001 PROPAGATION (S1165): server 422 field_errors → inline (WCAG 3.3.1)
          const fieldErrMap = mapFieldErrors(error);
          if (fieldErrMap) {
            setFormErrors((prev) => ({ ...prev, ...fieldErrMap }));
            focusFirstInvalidField(fieldErrMap);
            shouldShowSuccessRef.current = false;
            return;
          }
          const errorMessage = error.response?.data?.error?.user_message ||
                               error.response?.data?.error?.message ||
                               error.response?.data?.detail || '';
          if (errorMessage.includes('ชื่อ') && errorMessage.includes('มีอยู่ในระบบแล้ว')) {
            // OBS-S1109-001 FIX (S1113): BUSINESS_RULE_VIOLATION on name dup → inline field error
            setFormErrors((prev) => ({ ...prev, name: errorMessage }));
            shouldShowSuccessRef.current = false;
            const nameEl = document.getElementsByName('name')[0] as HTMLInputElement | undefined;
            if (nameEl) nameEl.focus();
          } else if (errorMessage.includes('รหัส') && errorMessage.includes('มีอยู่ในระบบแล้ว')) {
            // รหัสซ้ำ → ออกรหัสใหม่ แสดงเตือน รอผู้ใช้กดบันทึกอีกครั้ง (ไม่บันทึกอัตโนมัติ)
            const oldCode = formData.code;
            try {
              const newCode = await codeGeneratorService.generateSingleCode('site');
              setFormData({ ...formData, code: newCode });
              setCodeError(`รหัส ${oldCode} ไม่ว่างเนื่องจากมีการใช้งานแล้ว กรุณาบันทึกใหม่`);
            } catch (genError) {
              console.error('Error generating new code:', genError);
              setCodeError('ไม่สามารถสร้างรหัสใหม่ได้ กรุณาลองใหม่');
            }
            shouldShowSuccessRef.current = false;
          } else {
            showAlert('เกิดข้อผิดพลาด', errorMessage || 'ไม่สามารถเพิ่มโครงการได้', 'error');
          }
        },
      });
      return;
    }

    if (!site) return;
    // E2E v6 OBS-3: กล่องยืนยันปิดโครงการที่ยังมีการเช่า/สัญญาใช้งานอยู่ — ใช้ทั้งค่านับที่โหลดมา (pre-check) และ
    // 409 SITE_HAS_ACTIVE_USAGE ของ backend (ค่านับ stale) → กดยืนยันแล้วส่ง confirm_deactivate=true ซ้ำ
    const confirmDeactivate = (activeRentals: number, activeContracts: number) => {
      const usage = [
        activeRentals > 0 ? `การเช่าใช้งานอยู่ ${activeRentals} รายการ` : '',
        activeContracts > 0 ? `สัญญาใช้งานอยู่ ${activeContracts} ฉบับ` : '',
      ].filter(Boolean).join(' และ');
      showConfirm(
        'ยืนยันการปิดโครงการ',
        `โครงการ ${site.name} ยังมี${usage} ` +
        'การปิดใช้งานจะทำให้สร้างรายการเช่าใหม่บนโครงการนี้ไม่ได้ (รายการเช่า/สัญญาเดิมไม่ถูกกระทบ) ต้องการปิดใช้งานหรือไม่?',
        () => submitSiteUpdate(true),
        { isDanger: true, confirmText: 'ปิดใช้งาน', cancelText: 'ยกเลิก' }
      );
    };

    // P-S468-1: แนบ updated_at ที่โหลดมา → server ตรวจ 409 กันแก้ทับกันเงียบ
    const submitSiteUpdate = (confirmDeactivateFlag = false) => {
      isSubmittingRef.current = true;
      return updateSiteMutation.mutate({
        id: site.id,
        data: { ...siteData, ...(confirmDeactivateFlag ? { confirm_deactivate: true } : {}) },
        ifMatch: siteIfMatch(site),
      }, {
        onSettled: releaseSubmit,
        onSuccess: async () => {
          await commitAppCredentials(site.id);
          setCodeError('');
          finish(site.id);
        },
        onError: (error: any) => {
          console.error('Error updating site:', error);
          if (error.response?.status === 409) {
            const envelope = error.response?.data?.error;
            const details = envelope?.details ?? {};
            const reason = details.reason ?? envelope?.reason;
            if (reason === 'SITE_HAS_ACTIVE_USAGE') {
              confirmDeactivate(Number(details.active_rental_count) || 0, Number(details.active_contract_count) || 0);
              return;
            }
            // P-S601-1: 409 stale-edit — มีคนแก้โครงการนี้ไปก่อน (useMutationWithRefresh แจ้งเตือนข้อความไทยให้แล้ว)
            // โหลดค่าล่าสุดจาก server ลงฟอร์มใหม่ (ได้ token ใหม่ด้วย) — ห้ามคงค่าที่พิมพ์ไว้แล้วให้กดซ้ำด้วย token ใหม่
            // เพราะเท่ากับเขียนทับงานของอีกคนโดยไม่เห็นว่าเขาแก้อะไร (แบบเดียวกับ CompanyFormPage)
            initializedFor.current = null;
            siteQuery.refetch();
            return;
          }
          // BUG-S1162-001 PROPAGATION (S1165): server 422 field_errors → inline (WCAG 3.3.1)
          const fieldErrMap = mapFieldErrors(error);
          if (fieldErrMap) {
            setFormErrors((prev) => ({ ...prev, ...fieldErrMap }));
            focusFirstInvalidField(fieldErrMap);
            return;
          }
          const errorMessage = error.response?.data?.error?.user_message ||
                               error.response?.data?.error?.message ||
                               error.response?.data?.detail ||
                               'ไม่สามารถแก้ไขโครงการได้';
          if (errorMessage.includes('ชื่อ') && errorMessage.includes('มีอยู่ในระบบแล้ว')) {
            setFormErrors((prev) => ({ ...prev, name: errorMessage }));
            const nameEl = document.getElementsByName('name')[0] as HTMLInputElement | undefined;
            if (nameEl) nameEl.focus();
          } else {
            showAlert('เกิดข้อผิดพลาด', errorMessage, 'error');
          }
        },
      });
    };

    // Phase 2 สถานะโครงการ: จะปิดโครงการที่ยังมีการเช่า/สัญญาใช้งานอยู่ → ต้องยืนยันก่อน
    // (เกณฑ์เดียวกับ guard ฝั่ง backend: rental ACTIVE/PENDING_CANCELLATION หรือ contract ACTIVE)
    const activeRentals = site.active_rental_count || 0;
    const activeContracts = site.active_contract_count || 0;
    if (siteData.status === 'inactive' && site.status !== 'inactive' && (activeRentals > 0 || activeContracts > 0)) {
      confirmDeactivate(activeRentals, activeContracts);
      return;
    }
    submitSiteUpdate();
  };

  const title = isEdit ? 'แก้ไขข้อมูลโครงการ' : 'เพิ่มโครงการใหม่';
  const breadcrumbs = useMemo(() => [
    { label: 'ข้อมูลโครงการ', path: backTo },
    ...(isEdit && site ? [{ label: site.name, path: siteDetailUrl(site.id) }] : []),
    { label: isEdit ? 'แก้ไข' : 'เพิ่มใหม่' },
  ], [backTo, isEdit, site]);

  const notFound = isEdit && siteId === null ? { response: { status: 404 } } : null;

  return (
    <DetailPageLayout
      breadcrumbs={breadcrumbs}
      icon={<RiMapPinLine />}
      title={title}
      recordId={isEdit ? site?.code || siteId : undefined}
      subtitle={isEdit ? site?.name : 'กรอกข้อมูลโครงการ (รหัสสร้างอัตโนมัติ)'}
      backTo={cancelTo}
      loading={(isEdit && siteQuery.isLoading) || (initialSnapshot === null && !notFound && !siteQuery.isError)}
      error={notFound || siteQuery.error}
      notFoundMessage="ไม่พบโครงการที่ระบุ"
      onRetry={() => siteQuery.refetch()}
      overlays={
        <ConfirmModal
          isOpen={showModal}
          onClose={onCancelLeave}
          onConfirm={onConfirmLeave}
          pushHistoryOnOpen={false}
          title="ข้อมูลยังไม่ได้บันทึก"
          message="คุณมีข้อมูลที่ยังไม่ได้บันทึก หากออกจากหน้านี้ข้อมูลที่กรอกจะหายไป ต้องการออกจากหน้านี้หรือไม่?"
          confirmText="ออกจากหน้านี้"
          cancelText="อยู่ต่อ"
          isDanger={true}
        />
      }
      sidebar={
        isEdit && site ? (
          <>
            <RecordMetaCard
              createdAt={site.created_at}
              createdBy={site.created_by}
              updatedAt={site.updated_at}
              updatedBy={site.updated_by}
            />
            <DetailSection title="ประวัติกิจกรรม" icon={<RiHistoryLine />}>
              <EntityActivityTimeline entityType="site" entityId={site.id} fieldLabels={SITE_FIELD_LABELS} />
            </DetailSection>
          </>
        ) : undefined
      }
    >
      <DetailSection title="ข้อมูลโครงการ" icon={<RiMapPinLine />}>
        {/* ไม่ใช้ <form onSubmit>: Enter ในช่องค้นหาของ SearchableSelect จะกลายเป็นกดบันทึก (โมดัลเดิมก็ไม่ใช้ native submit) */}
        <div>
          <SiteFormFields
            mode={isEdit ? 'edit' : 'create'}
            siteId={site?.id}
            formData={formData}
            setFormData={setFormData}
            contacts={contacts}
            setContacts={setContacts}
            formErrors={formErrors}
            setFormErrors={setFormErrors}
            codeError={codeError}
            companies={companies}
            companiesLoading={companiesLoading}
            companiesError={companiesError}
            canManageAppCredentials={canManageAppCredentials}
            appCredRef={appCredRef}
          />
          <div className="mt-6 pt-4 border-t border-secondary-200 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
            <Link
              to={cancelTo}
              className="inline-flex items-center justify-center px-5 py-2.5 border border-secondary-300 rounded-lg text-sm font-medium text-secondary-700 bg-white hover:bg-secondary-50 transition-all shadow-button"
            >
              <RiCloseLine className="w-4 h-4 mr-1.5" />
              ยกเลิก
            </Link>
            <button
              type="button"
              onClick={handleSaveSite}
              disabled={saving}
              className="inline-flex items-center justify-center px-6 py-2.5 border border-transparent rounded-lg text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all shadow-button disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RiCheckboxCircleLine className="w-4 h-4 mr-1.5" />
              {saving ? 'กำลังบันทึก...' : 'บันทึกข้อมูล'}
            </button>
          </div>
        </div>
      </DetailSection>
    </DetailPageLayout>
  );
};

export default SiteFormPage;
